import {
  PARTNER_WHATSAPP_DEALER_HEALTH_LABELS,
  PARTNER_WHATSAPP_DEALER_HEALTH_STATUS_FILTERS,
  buildPartnerWhatsappDealerHealthHref,
} from "../constants/partnerWhatsappMonitoringConstants";

const EMPTY_SUMMARY = Object.freeze({
  totalDealers: 0,
  ready: 0,
  needsAttention: 0,
  missingWhatsapp: 0,
  invalidPhone: 0,
  notSynced: 0,
  lowResponse: 0,
  manualOutreachHeavy: 0,
});

const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
});

export function partnerWhatsappDealerHealthLabel(status) {
  if (!status) return "Unknown";
  return PARTNER_WHATSAPP_DEALER_HEALTH_LABELS[status] || "Unknown";
}

export function formatDealerHealthRate(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Math.round(Number(value) * 1000) / 10}%`;
}

export function formatDealerHealthDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function parseDealerHealthSearchParams(searchParams) {
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(searchParams.get("pageSize")) || 25));
  const range = searchParams.get("range") === "7d" ? "7d" : "30d";
  const statusParam = searchParams.get("status") || "all";
  const allowedStatuses = PARTNER_WHATSAPP_DEALER_HEALTH_STATUS_FILTERS.map((entry) => entry.value);
  const status = allowedStatuses.includes(statusParam) ? statusParam : "all";
  const sort = [
    "dealerName",
    "healthStatus",
    "acceptRate",
    "manualOutreachCount",
    "failedAlertCount",
    "lastActivityAt",
  ].includes(searchParams.get("sort"))
    ? searchParams.get("sort")
    : "healthStatus";
  const direction = searchParams.get("direction") === "asc" ? "asc" : "desc";

  return {
    page,
    pageSize,
    range,
    status,
    search: searchParams.get("search")?.trim() || "",
    sort,
    direction,
  };
}

export function buildDealerHealthSearchParams(filters = {}) {
  const params = new URLSearchParams();
  if (filters.range === "7d") {
    params.set("range", "7d");
  }
  if (filters.status && filters.status !== "all") {
    params.set("status", filters.status);
  }
  if (filters.search) {
    params.set("search", filters.search);
  }
  if (filters.sort && filters.sort !== "healthStatus") {
    params.set("sort", filters.sort);
  }
  if (filters.direction && filters.direction !== "desc") {
    params.set("direction", filters.direction);
  }
  if (filters.page && filters.page > 1) {
    params.set("page", String(filters.page));
  }
  if (filters.pageSize && filters.pageSize !== 25) {
    params.set("pageSize", String(filters.pageSize));
  }
  return params;
}

export function dealerHealthHasActiveFilters(filters = {}) {
  return Boolean(
    filters.search ||
      (filters.status && filters.status !== "all") ||
      filters.range === "7d" ||
      (filters.sort && filters.sort !== "healthStatus") ||
      (filters.direction && filters.direction !== "desc")
  );
}

export function normalizePartnerWhatsappDealerHealth(body = {}) {
  const summary = { ...EMPTY_SUMMARY, ...(body.summary || {}) };
  const pagination = { ...DEFAULT_PAGINATION, ...(body.pagination || {}) };
  const items = Array.isArray(body.items)
    ? body.items.map((item) => ({
        ...item,
        healthLabel: item.healthLabel || partnerWhatsappDealerHealthLabel(item.healthStatus),
      }))
    : [];

  return {
    success: body.success !== false,
    range: body.range === "7d" ? "7d" : "30d",
    items,
    summary,
    pagination,
    filters: body.filters || {},
    redactionPolicyVersion: body.redactionPolicyVersion || null,
  };
}

export function buildDealerHealthFilterHref(basePath, status) {
  return buildPartnerWhatsappDealerHealthHref(basePath, { status });
}

export function partnerWhatsappDealerHealthContainsSensitiveFields(payload) {
  const serialized = JSON.stringify(payload || {});
  const sensitivePatterns = [
    /rawPayload/i,
    /claimToken/i,
    /apiKey/i,
    /x-api-key/i,
    /leadBrokerApiKey/i,
    /\+57\d{10}/,
  ];
  return sensitivePatterns.some((pattern) => pattern.test(serialized));
}
